import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import { TEXT_FILTER_MAP } from 'Components/Generic/AdvancedDataTable/Helper';
import UserMultiselect from 'Components/Generic/BOSMultiselect/UserMultiselect';
import BOSSelect from 'Components/Generic/BOSSelect';
import QuickViewButton from 'Components/Generic/Buttons/QuickViewButton';
import StatusButton from 'Components/Generic/Buttons/StatusButton';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import { getUserNameFromId } from 'Data/context/Users';
import {
  getObjectStatus,
  OBJECT_STATUS,
  user_org_roles,
  user_roles,
} from 'Data/Enum';
import OrgData from 'Data/OrgData';
import SiteDrawerData from 'Data/SiteDrawerData';
import DataManager from 'Lib/DataManager';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { isUserAuthorized } from 'Util';

let DM = new DataManager();

let filterOptions = {
  name: {
    seqRestKey: '*name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  short_name: {
    seqRestKey: '*short_name',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  pi_id: {
    seqRestKey: '*pi_id',
  },
  delivery_code: {
    seqRestKey: '*delivery_code',
    mapToSeqRestValue: TEXT_FILTER_MAP,
  },
  status: {
    seqRestKey: '*status',
  },
};

const OrganizationListView = ({ addNewOrgButton }) => {
  const [data, setData] = useState();
  const [refreshKey, setRefreshKey] = useState(0);
  const [persistentFilter, updateFilterField] =
    usePersistentFiltering(`ORG_FILTER`);

  useEffect(() => {
    DM.add(OrgData.registerListener('LIST_LOADED', setData), OrgData);
    DM.add(
      OrgData.registerListener('LIST_LOAD_ERROR', onOrgListLoadError),
      OrgData,
    );
    DM.add(OrgData.registerListener('RECORD_CREATED', refreshData), OrgData);
    DM.add(OrgData.registerListener('RECORD_UPDATED', refreshData), OrgData);
    DM.add(
      OrgData.registerListener('RECORD_ACTIVATED', refreshAndCloseDrawer),
      OrgData,
    );
    DM.add(
      OrgData.registerListener('RECORD_DEACTIVATED', refreshAndCloseDrawer),
      OrgData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  const onOrgListLoadError = () => {};

  const refreshData = () => {
    setRefreshKey((prevState) => prevState + 1);
  };
  const refreshAndCloseDrawer = () => {
    refreshData();
    SiteDrawerData.close();
  };

  const headers = [
    {
      label: 'Name',
      value: 'name',
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Name'}
            defaultValue={persistentFilter?.name}
            onFilterValueUpdate={(e) =>
              updateFilterField('name', e.target.value)
            }
          />
        );
      },
      render(org) {
        return isUserAuthorized({
          roles: [
            user_roles.ADMINISTRATOR,
            [`${org.id}`, user_org_roles.MANAGER],
          ],
        }) ? (
          <Link to={`./${org.id}`}>{org.name}</Link>
        ) : (
          org.name
        );
      },
    },

    {
      label: 'Short Name',
      value: 'short_name',
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Short Name'}
            defaultValue={persistentFilter?.short_name}
            onFilterValueUpdate={(e) =>
              updateFilterField('short_name', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'PI',
      value: 'pi_id',
      filterable: true,
      renderHeader() {
        return (
          <UserMultiselect
            labelText='PI'
            onSelectedUsersUpdate={(userList) =>
              updateFilterField('pi_id', userList)
            }
            defaultSelectedItems={persistentFilter?.pi_id}
            isHeader
          />
        );
      },
      format: (pi_id) => {
        return <UserNameWithDrawerButton userId={pi_id} />;
      },
      getText: (org) => {
        return getUserNameFromId(org.pi_id);
      },
    },

    {
      label: 'Delivery Code',
      value: 'delivery_code',
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Delivery Code'}
            defaultValue={persistentFilter?.delivery_code}
            onFilterValueUpdate={(e) =>
              updateFilterField('delivery_code', e.target.value)
            }
          />
        );
      },
    },

    {
      label: 'Status',
      value: 'status',
      width: 120,
      centeredHeader: true,
      filterable: true,
      renderHeader() {
        return (
          <BOSSelect
            listData={Object.values(OBJECT_STATUS)}
            idField={'key'}
            labelField={'label'}
            labelText='Status'
            onItemSelect={(item) => updateFilterField('status', [item])}
            defaultSelectedId={
              persistentFilter?.status?.length
                ? persistentFilter.status[0].key
                : undefined
            }
            isHeader
          />
        );
      },
      format: (status) => {
        return <StatusButton statusKey={status} />;
      },
      getText: (org) => getObjectStatus(org.status)?.label || org.status,
    },

    {
      label: '',
      value: 'quick_view',
      isIconCol: true,
      render(org) {
        return (
          <OrgNameWithDrawerButton
            editable={isUserAuthorized({
              roles: [
                user_roles.ADMINISTRATOR,
                [`${org.id}`, user_org_roles.MANAGER],
              ],
            })}
            orgId={org.id}
            label={<QuickViewButton />}
          />
        );
      },
    },
  ];

  const isOrgInactive = (org) => {
    return org?.status === OBJECT_STATUS.DEACTIVATED.key || false;
  };

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={'Manage Core Facilities'}
      dataKey={'ORG'}
      data={data}
      dataQueryFunc={OrgData.getOrgList}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      defaultSort={'name'}
      actionButtons={addNewOrgButton}
      highlightFunc={isOrgInactive}
    />
  );
};

OrganizationListView.propTypes = {
  addNewOrgButton: PropTypes.node,
};

export default OrganizationListView;
